import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { useUpdateCampania } from '@/modules/multichannel/hooks/useCampanias'
import { Loader2 } from 'lucide-react'
import type { Campania } from '@/modules/multichannel/types/campania'

interface EstadosEditDialogProps {
  campania: Campania
  open: boolean
  onOpenChange: (open: boolean) => void
}

const ESTADOS_CONTENIDO = [
  'Elaborado HTML',
  'Elaborado HTML y Whatsapp',
  'Elaborado Whatsapp',
  'En proceso',
  'Finalizado',
  'PDF creado',
  'Pendiente',
]

const ESTADOS_ENVIO = ['Enviado', 'Pendiente', 'Programado']

const ESTADOS_REPORTE = ['Generado', 'Pendiente']

export function EstadosEditDialog({
  campania,
  open,
  onOpenChange,
}: EstadosEditDialogProps) {
  const [estadoContenido, setEstadoContenido] = useState(
    campania.estado_contenido
  )
  const [estadoEnvio, setEstadoEnvio] = useState(campania.estado_envio)
  const [estadoReporte, setEstadoReporte] = useState(campania.estado_reporte)

  const updateMutation = useUpdateCampania()

  const handleSubmit = async () => {
    try {
      await updateMutation.mutateAsync({
        id: campania.id,
        data: {
          estado_contenido: estadoContenido,
          estado_envio: estadoEnvio,
          estado_reporte: estadoReporte,
        },
      })
      onOpenChange(false)
    } catch (error) {
      console.error('Error al actualizar estados:', error)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Editar Estados</DialogTitle>
          <DialogDescription>
            Actualiza los estados de la campaña: {campania.nombre_campania}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Estado Contenido */}
          <div className="space-y-2">
            <Label htmlFor="estado-contenido">Estado de Contenido</Label>
            <Select value={estadoContenido} onValueChange={setEstadoContenido}>
              <SelectTrigger id="estado-contenido">
                <SelectValue placeholder="Selecciona un estado" />
              </SelectTrigger>
              <SelectContent>
                {ESTADOS_CONTENIDO.map((estado) => (
                  <SelectItem key={estado} value={estado}>
                    {estado}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Estado Envío */}
          <div className="space-y-2">
            <Label htmlFor="estado-envio">Estado de Envío</Label>
            <Select value={estadoEnvio} onValueChange={setEstadoEnvio}>
              <SelectTrigger id="estado-envio">
                <SelectValue placeholder="Selecciona un estado" />
              </SelectTrigger>
              <SelectContent>
                {ESTADOS_ENVIO.map((estado) => (
                  <SelectItem key={estado} value={estado}>
                    {estado}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Estado Reporte */}
          <div className="space-y-2">
            <Label htmlFor="estado-reporte">Estado de Reporte</Label>
            <Select value={estadoReporte} onValueChange={setEstadoReporte}>
              <SelectTrigger id="estado-reporte">
                <SelectValue placeholder="Selecciona un estado" />
              </SelectTrigger>
              <SelectContent>
                {ESTADOS_REPORTE.map((estado) => (
                  <SelectItem key={estado} value={estado}>
                    {estado}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={updateMutation.isPending}
            className="bg-teal-800 hover:bg-teal-900 text-white"
          >
            {updateMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Guardar Cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
